import URL from 'url-parse';
import { getCTAShortcode } from 'utils/metadata';
import { hostnameRegex } from 'config/pathRegex';
import { isNode } from 'utils/environment';
import getRuntimeConfiguration from 'utils/getRuntimeConfiguration';
import { checkUsEnglish } from 'utils/checkUsEnglish';
import {
  ENGLISH,
  ENGLISH_USA,
  FRENCH,
  GERMAN,
  ITALIAN,
  POLISH,
  PORTUGUESE,
  SPANISH,
  SWEDISH,
} from 'config/types/language';

// Local Variables
const pathLanguageCodeRegex = /^\/([a-zA-Z-]+)\//;
const isAbsoluteRegex = /^(?:[a-z]+:)?\/\//;
const isMailtoRegex = /^mailto:/;
const stagingDomainExtension = 'cn';

// Exported Functions
export const isAbsolute = (url) => {
  if ('string' !== typeof url) {
    return false;
  }

  const matches = url.match(isAbsoluteRegex);
  return matches && matches[0].length;
};

export const isMailto = (url) =>
  isMailtoRegex.test(url);

const herokuHostnames = [
  'babbel-magazine-qa.herokuapp.com',
  'babbel-magazine-staging.herokuapp.com',
];

export const isHerokuHostname = (hostname) => herokuHostnames.includes(hostname);

const isLocalHost = (hostname) => hostname.startsWith('localhost');

export const isBabbelHostname = (hostname) =>
  hostnameRegex.test(hostname);

export const isTrackingDisabled = !isNode() && (
  (new URLSearchParams(window.location.search)).has('disableTracking')
);

export const getLanguageHostname = (hostname, languageCode) => {
  if (!isBabbelHostname(hostname)) {
    return hostname;
  }

  const domainSuffix = hostname.match(hostnameRegex)[1];
  switch (languageCode) {
    case FRENCH: return `fr.${domainSuffix}`;
    case GERMAN: return `de.${domainSuffix}`;
    case ITALIAN: return `it.${domainSuffix}`;
    case POLISH: return `pl.${domainSuffix}`;
    case PORTUGUESE: return `pt.${domainSuffix}`;
    case SPANISH: return `es.${domainSuffix}`;
    case SWEDISH: return `se.${domainSuffix}`;

    case ENGLISH:
    case ENGLISH_USA:
    default: return `www.${domainSuffix}`;
  }
};

export const getLanguageCodeFromPath = (path) => {
  const matches = path.match(pathLanguageCodeRegex);
  if (matches && 1 in matches) {
    return matches[1];
  }
  return '';
};

export const getApigatewayBaseUrl = () =>
  getRuntimeConfiguration('APIGATEWAY_BASE_URL') || 'https://api.babbel-staging.io/gamma';

export const getGoBaseUrl = () =>
  getRuntimeConfiguration('GO_BASE_URL') || 'https://go.babbel.cn';

export const getAwsSessionEndpoint = () =>
  getRuntimeConfiguration('AWS_SESSION_ENDPOINT') || '/aws_session.json';

export const getAwsSessionMethod = () =>
  getRuntimeConfiguration('AWS_SESSION_METHOD') || 'GET';

export const getLinkParts = (link) =>
  new URL(link);

export const convertPathLanguageCode = (path, newLanguageCode) =>
  path.replace(pathLanguageCodeRegex, `/${newLanguageCode}/`);

export const buildShortcodeUrl = (shortcode, suffix = 'default.gif') =>
  `${getGoBaseUrl()}/${shortcode}/${suffix}`;

// Can be used for client- or server-side rendering
export const getHostname = () => (
  isNode() ? process.env.REQUEST_HOSTNAME : window.location.hostname
);

export const hostIsStaging = (hostname) =>
  isHerokuHostname(hostname) ||
  hostname.endsWith(stagingDomainExtension) ||
  isLocalHost(hostname);

const currentHostIsStaging = () => hostIsStaging(getHostname());

const getCurrentDomainExtension = () => (currentHostIsStaging() ? stagingDomainExtension : 'com');

export const getBabbelBaseUrl = () => `https://www.babbel.${getCurrentDomainExtension()}`;

export const prependEditionHostname = (languageCode, path = '/') => {
  const currentHostname = getHostname();
  const newHostname = getLanguageHostname(currentHostname, languageCode);

  // For staging, QA and localhost, our URLs can be based off of Heroku, for staging
  // we want this to keep to the same domain so we don't do full page loads
  if (currentHostIsStaging() && isHerokuHostname(newHostname)) {
    return path;
  }

  // Use a relative path only when intending to stay within the current hostname so
  // this app remains a SPA instead of doing full page loads
  const url = newHostname === currentHostname ?
    path : `//${newHostname}${path}`;

  return url;
};

export const buildCanonicalLink = (currentEdition, relPath) =>
  `https://${getLanguageHostname('www.babbel.com', currentEdition)}${relPath}`;

// Input: Given the currentEdition (i.e., en, es, fr)
// Output: provide absolute homepageUrl
// Context: use in NotFound component to redirect the 404.
// Note: currently magazine only offered in USA English, no redirect to UK site
export const getBabbelHomepageUrl = (currentEdition) => {
  switch (currentEdition) {
    case FRENCH: return 'https://fr.babbel.com/?locale=fr';
    case GERMAN: return 'https://de.babbel.com/?locale=de';
    case ITALIAN: return 'https://it.babbel.com/?locale=it';
    case POLISH: return 'https://pl.babbel.com/?locale=pl';
    case PORTUGUESE: return 'https://pt.babbel.com/?locale=pt';
    case SPANISH: return 'https://es.babbel.com/?locale=es';
    case SWEDISH: return 'https://se.babbel.com/?locale=sv';
    case ENGLISH:
    case ENGLISH_USA:
    default: return 'https://www.babbel.com/';
  }
};

// Input: Given the currentEdition (i.e., en, es, fr)
// Output: provide absolute rss url
// Context: use in SocialLinks component to generate the correct link for the RSS icon
export const getBabbelRSSUrl = (currentEdition) => {
  const rssUrlPrefix = 'https://rss.babbel.news/feed/?post_type=';
  switch (currentEdition) {
    case FRENCH: return `${rssUrlPrefix}french_edition`;
    case GERMAN: return `${rssUrlPrefix}german_edition`;
    case ITALIAN: return `${rssUrlPrefix}italian_edition`;
    case POLISH: return `${rssUrlPrefix}polish_edition`;
    case PORTUGUESE: return `${rssUrlPrefix}portuguese_edition`;
    case SPANISH: return `${rssUrlPrefix}spanish_edition`;
    case SWEDISH: return `${rssUrlPrefix}swedish_edition`;
    case ENGLISH:
    case ENGLISH_USA:
    default: return `${rssUrlPrefix}english_edition`;
  }
};

export const getInstagramUrl = (currentEdition) => {
  const instagramUrlPrefix = 'https://www.instagram.com/';
  switch (currentEdition) {
    case ENGLISH:
    case ENGLISH_USA: return `${instagramUrlPrefix}babbelusa/`;
    case FRENCH: return `${instagramUrlPrefix}babbel_fr/`;
    case GERMAN: return `${instagramUrlPrefix}babbel_de/`;
    case PORTUGUESE: return `${instagramUrlPrefix}babbel_pt/`;
    case SPANISH: return `${instagramUrlPrefix}babbel_es/`;
    default: return `${instagramUrlPrefix}babbel`;
  }
};

export const getSocialLinks = (currentEdition) => ({
  twitter: 'https://twitter.com/babbel',
  facebook: 'https://www.facebook.com/babbel.languages',
  youtube: 'https://www.youtube.com/user/LessonNine',
  instagram: getInstagramUrl(currentEdition),
  linkedin: 'https://www.linkedin.com/company/babbel-com',
});

export const buildHomepageLink = (rawLanguageCode, link) => {
  const convertedLanguageCode = checkUsEnglish(rawLanguageCode);
  const rawPath = getLinkParts(link).pathname;
  const convertedPath = convertPathLanguageCode(rawPath, convertedLanguageCode);

  const fullUrl = prependEditionHostname(convertedLanguageCode, convertedPath);
  return fullUrl;
};

export const buildCTALink = (currentEdition) => {
  const suffix = 'default'; // Suffix for CTA go-links

  const shortcode = getCTAShortcode(currentEdition);
  return buildShortcodeUrl(shortcode, suffix);
};
